/**
 * Analytics utility functions
 * Centralized event tracking with type safety
 */

import { logEvent as firebaseLogEvent, setUserProperties } from 'firebase/analytics'
import { analytics } from '@/config/firebase'

/**
 * Log a custom event to Google Analytics
 */
export const logEvent = (eventName: string, eventParams?: Record<string, any>) => {
  if (!analytics) return

  try {
    firebaseLogEvent(analytics, eventName, eventParams)
  } catch (error) {
    console.error('Analytics error:', error)
  }
}

/**
 * Log page view
 */
export const logPageView = (pagePath: string, pageTitle?: string) => {
  logEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
  })
}

/**
 * Set user properties for segmentation
 */
export const setAnalyticsUserProperties = (properties: Record<string, any>) => {
  if (!analytics) return

  try {
    setUserProperties(analytics, properties)
  } catch (error) {
    console.error('Analytics error:', error)
  }
}

/**
 * Analytics events for the wishlist app
 * Pre-defined events with consistent naming and parameters
 */
export const analyticsEvents = {
  // ===== USER EVENTS =====

  /**
   * User completed signup
   * @param method - Authentication method used (google/anonymous)
   */
  signupCompleted: (method: 'google' | 'anonymous') => {
    logEvent('signup_completed', { method })
  },

  /**
   * User linked anonymous account to Google
   */
  accountLinked: () => {
    logEvent('account_linked', {})
  },

  // ===== WISHLIST EVENTS =====

  /**
   * User created a new wishlist
   * @param wishlistId - ID of the created wishlist
   * @param occasion - Occasion type (birthday, christmas, etc.)
   * @param itemCount - Initial number of items (usually 0)
   */
  wishlistCreated: (wishlistId: string, occasion: string, itemCount = 0) => {
    logEvent('wishlist_created', {
      wishlist_id: wishlistId,
      occasion,
      item_count: itemCount,
    })
  },

  /**
   * User viewed a wishlist
   * @param wishlistId - ID of the viewed wishlist
   * @param isOwner - Whether the viewer is the owner
   */
  wishlistViewed: (wishlistId: string, isOwner: boolean) => {
    logEvent('wishlist_viewed', {
      wishlist_id: wishlistId,
      is_owner: isOwner,
    })
  },

  /**
   * User shared a wishlist
   * @param wishlistId - ID of the shared wishlist
   * @param method - Sharing method (copy_link, native_share, etc.)
   */
  wishlistShared: (wishlistId: string, method: string) => {
    logEvent('wishlist_shared', {
      wishlist_id: wishlistId,
      method,
    })
  },

  /**
   * User deleted a wishlist
   * @param wishlistId - ID of the deleted wishlist
   * @param itemCount - Number of items in the wishlist
   */
  wishlistDeleted: (wishlistId: string, itemCount: number) => {
    logEvent('wishlist_deleted', {
      wishlist_id: wishlistId,
      item_count: itemCount,
    })
  },

  // ===== ITEM EVENTS =====

  /**
   * User added an item to a wishlist
   * @param wishlistId - ID of the wishlist
   * @param itemId - ID of the added item
   * @param hasUrl - Whether the item has a URL
   * @param hasPrice - Whether the item has a price
   */
  itemAdded: (
    wishlistId: string,
    itemId: string,
    hasUrl: boolean,
    hasPrice: boolean
  ) => {
    logEvent('item_added', {
      wishlist_id: wishlistId,
      item_id: itemId,
      has_url: hasUrl,
      has_price: hasPrice,
    })
  },

  /**
   * User updated an item
   * @param wishlistId - ID of the wishlist
   * @param itemId - ID of the updated item
   */
  itemUpdated: (wishlistId: string, itemId: string) => {
    logEvent('item_updated', {
      wishlist_id: wishlistId,
      item_id: itemId,
    })
  },

  /**
   * User deleted an item
   * @param wishlistId - ID of the wishlist
   * @param itemId - ID of the deleted item
   */
  itemDeleted: (wishlistId: string, itemId: string) => {
    logEvent('item_deleted', {
      wishlist_id: wishlistId,
      item_id: itemId,
    })
  },

  // ===== RESERVATION EVENTS =====

  /**
   * User reserved an item
   * @param wishlistId - ID of the wishlist
   * @param itemId - ID of the reserved item
   */
  itemReserved: (wishlistId: string, itemId: string) => {
    logEvent('item_reserved', {
      wishlist_id: wishlistId,
      item_id: itemId,
    })
  },

  /**
   * User unreserved an item
   * @param wishlistId - ID of the wishlist
   * @param itemId - ID of the unreserved item
   */
  itemUnreserved: (wishlistId: string, itemId: string) => {
    logEvent('item_unreserved', {
      wishlist_id: wishlistId,
      item_id: itemId,
    })
  },

  // ===== SECRET SANTA EVENTS =====

  /**
   * User created a Secret Santa event
   * @param eventId - ID of the Secret Santa event
   * @param participantCount - Number of participants
   * @param budget - Budget amount (if set)
   */
  secretSantaCreated: (
    eventId: string,
    participantCount: number,
    budget?: number
  ) => {
    logEvent('secret_santa_created', {
      event_id: eventId,
      participant_count: participantCount,
      budget: budget || null,
    })
  },

  /**
   * Secret Santa draw was completed
   * @param eventId - ID of the Secret Santa event
   * @param participantCount - Number of participants
   */
  secretSantaDrawCompleted: (eventId: string, participantCount: number) => {
    logEvent('secret_santa_draw_completed', {
      event_id: eventId,
      participant_count: participantCount,
    })
  },

  /**
   * User viewed their Secret Santa assignment
   * @param eventId - ID of the Secret Santa event
   */
  secretSantaAssignmentViewed: (eventId: string) => {
    logEvent('secret_santa_assignment_viewed', {
      event_id: eventId,
    })
  },

  /**
   * User joined a Secret Santa event
   * @param eventId - ID of the Secret Santa event
   */
  secretSantaJoined: (eventId: string) => {
    logEvent('secret_santa_joined', {
      event_id: eventId,
    })
  },

  // ===== ENGAGEMENT EVENTS =====

  /**
   * User clicked a share button
   * @param shareType - Type of share (copy_link, native_share, etc.)
   */
  shareButtonClicked: (shareType: 'copy_link' | 'native_share') => {
    logEvent('share', {
      method: shareType,
    })
  },

  /**
   * User viewed their reservations page
   */
  reservationsViewed: () => {
    logEvent('reservations_viewed', {})
  },

  /**
   * User clicked on an external item link
   * @param itemId - ID of the item
   * @param domain - Domain of the external link
   */
  externalLinkClicked: (itemId: string, domain: string) => {
    logEvent('external_link_clicked', {
      item_id: itemId,
      domain,
    })
  },
}

/**
 * Track conversion events (for funnel analysis)
 */
export const conversionEvents = {
  /**
   * User completed onboarding (created first wishlist + added first item)
   */
  onboardingCompleted: () => {
    logEvent('onboarding_completed', {})
  },

  /**
   * User became active (shared a wishlist or reserved an item)
   */
  userActivated: () => {
    logEvent('user_activated', {})
  },

  /**
   * User invited friends (via referral link)
   * @param count - Number of invites sent
   */
  friendsInvited: (count: number) => {
    logEvent('friends_invited', {
      count,
    })
  },
}
